import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { ArrowLeft, AlertCircle, RotateCcw, FileSearch, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from '../components/ui/dialog';
import { Actions as IMPORT_ACTIONS } from '../store/actions/intelligentImportActions';
import useImportPolling from '../hooks/useImportPolling';
import { PIPELINE_STATUSES } from '../constants/importConstants';
import PipelineProgress from '../components/IntelligentImport/PipelineProgress';
import ClassificationResolver from '../components/IntelligentImport/ClassificationResolver';
import DateFormatWarning from '../components/IntelligentImport/DateFormatWarning';
import StagingTable from '../components/IntelligentImport/StagingTable';
import FactorMatchReview from '../components/IntelligentImport/FactorMatchReview';
import SingleDocumentReview from '../components/IntelligentImport/SingleDocumentReview';
import StagingSubmit from '../components/IntelligentImport/StagingSubmit';
import { importNoDataMessage, isImportNoData, resolveImportConnectorId } from '../components/Connections/importNoData';

const IntelligentImportJob = ({
  currentJob,
  error,
  getImportStatus,
  clearJob,
  retryMatch,
  commitImport,
  deleteImport,
  cyfAdmin,
  whiteLabelAdmin,
}) => {
  const { id } = useParams();
  const history = useHistory();
  const { polling, startPolling } = useImportPolling(id);
  const [statusOverride, setStatusOverride] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isImportAdmin = Boolean(cyfAdmin || whiteLabelAdmin);

  useEffect(() => {
    if (id) getImportStatus(id);
    return () => clearJob();
  }, [id]);

  const status = statusOverride || currentJob?.pipelineStatus;
  const workerStatus = String(currentJob?.functionStatus?.status || currentJob?.function_status?.status || '').toUpperCase();
  const workerRunning = workerStatus === 'RUNNING' || workerStatus === 'PENDING';
  const isProcessing =
    Boolean(status) &&
    ![
      PIPELINE_STATUSES.NEEDS_USER_CLASSIFICATION,
      PIPELINE_STATUSES.PENDING_REVIEW,
      PIPELINE_STATUSES.COMPLETE,
      PIPELINE_STATUSES.PARTIAL_COMPLETE,
      PIPELINE_STATUSES.CANCELLED,
      PIPELINE_STATUSES.FAILED,
    ].includes(status);

  useEffect(() => {
    if (!id || polling || !currentJob) return;
    if (isProcessing || workerRunning) startPolling();
  }, [id, polling, currentJob, isProcessing, workerRunning, startPolling]);

  const handleRetryMatch = useCallback(async () => {
    setRetrying(true);
    await retryMatch(id);
    setRetrying(false);
  }, [id, retryMatch]);

  const handleBackToConnections = useCallback(() => {
    const connectorId = resolveImportConnectorId(currentJob, null);
    history.push(connectorId ? `/connections/${connectorId}` : '/connections');
  }, [currentJob, history]);

  const handleRetryCommit = useCallback(async () => {
    setRetrying(true);
    await commitImport(id);
    setRetrying(false);
  }, [id, commitImport]);

  const handleBackToReview = useCallback(() => {
    setStatusOverride(PIPELINE_STATUSES.PENDING_REVIEW);
  }, []);

  const handleConfirmDeleteImport = useCallback(async () => {
    if (!id) return;
    setDeleting(true);
    const ok = await deleteImport(id);
    setDeleting(false);
    if (ok) {
      setDeleteOpen(false);
      history.push('/intelligent-import');
    }
  }, [id, deleteImport, history]);

  const renderContent = useMemo(() => {
    if (!currentJob) {
      return <div className='text-center py-20 text-muted-foreground'>Loading import data...</div>;
    }

    switch (status) {
      case PIPELINE_STATUSES.NEEDS_USER_CLASSIFICATION:
        return <ClassificationResolver importId={id} job={currentJob} />;

      case PIPELINE_STATUSES.PENDING_REVIEW:
        if (currentJob.dateFormatConfidence === 'ASSUMED' || currentJob.date_format_confidence === 'ASSUMED') {
          return <DateFormatWarning importId={id} job={currentJob} />;
        }
        if (currentJob.detectedSourceKind === 'DOCUMENT') {
          return <SingleDocumentReview importId={id} job={currentJob} />;
        }
        return <FactorMatchReview importId={id} job={currentJob} />;

      case PIPELINE_STATUSES.COMPLETE:
      case PIPELINE_STATUSES.FAILED:
        if (isImportNoData(currentJob)) {
          return (
            <div className='text-center py-20'>
              <AlertCircle size={40} className='mx-auto mb-3 text-muted-foreground' strokeWidth={1.5} />
              <p className='text-lg font-medium text-foreground'>No spend data</p>
              <p className='mt-2 text-sm text-muted-foreground max-w-md mx-auto'>
                {importNoDataMessage(currentJob)}
              </p>
              <Button variant='outline' className='mt-4' onClick={handleBackToConnections}>
                Back to Connections
              </Button>
            </div>
          );
        }
        if (status === PIPELINE_STATUSES.COMPLETE) {
          return <StagingSubmit importId={id} job={currentJob} />;
        }
        {
          const hasRows = (currentJob.totalRows || 0) > 0;
          const failedAtCommit = currentJob.failureStage === 'COMMITTING' || currentJob.failureStage === 'committing';
          const hasConfirmed = (currentJob.matchedRows || currentJob.confirmedRows || 0) > 0;
          return (
            <div className='text-center py-20'>
              <AlertCircle size={40} className='mx-auto mb-3 text-danger' strokeWidth={1.5} />
              <p className='text-lg font-medium text-danger'>Import Failed</p>
              {currentJob.lastError && (
                <p className='text-sm text-muted-foreground mt-2 max-w-md mx-auto'>{currentJob.lastError}</p>
              )}
              {currentJob.failureStage && (
                <p className='text-xs text-muted-foreground mt-1'>Failed at stage: {currentJob.failureStage}</p>
              )}
              <div className='flex flex-wrap justify-center gap-3 mt-6'>
                {hasRows && (
                  <Button variant='outline' onClick={handleBackToReview} disabled={retrying}>
                    <FileSearch size={16} strokeWidth={1.5} />
                    Review Staged Data
                  </Button>
                )}
                {failedAtCommit && hasConfirmed && (
                  <Button variant='outline' onClick={handleRetryCommit} loading={retrying}>
                    <RotateCcw size={16} strokeWidth={1.5} />
                    Retry Commit
                  </Button>
                )}
                {hasRows && !failedAtCommit && (
                  <Button variant='outline' onClick={handleRetryMatch} loading={retrying}>
                    <RotateCcw size={16} strokeWidth={1.5} />
                    Retry Matching
                  </Button>
                )}
                <Button variant='outline' onClick={() => history.push('/intelligent-import')}>
                  Back to Imports
                </Button>
              </div>
            </div>
          );
        }

      case PIPELINE_STATUSES.PARTIAL_COMPLETE:
        return <FactorMatchReview importId={id} job={currentJob} />;

      case PIPELINE_STATUSES.CANCELLED:
        return (
          <div className='text-center py-20'>
            <AlertCircle size={40} className='mx-auto mb-3 text-muted-foreground' strokeWidth={1.5} />
            <p className='text-lg font-medium text-muted-foreground'>Import Cancelled</p>
            <Button variant='outline' className='mt-4' onClick={() => history.push('/intelligent-import')}>
              Back to Imports
            </Button>
          </div>
        );

      default:
        return null;
    }
  }, [status, currentJob, id, history, handleBackToReview, handleBackToConnections, handleRetryMatch, handleRetryCommit, retrying]);

  return (
    <div className='flex flex-col gap-4 p-6'>
      <div className='flex flex-wrap items-center gap-3 justify-between'>
        <div className='flex items-center gap-3 min-w-0'>
          <Button variant='ghost' size='sm' onClick={() => history.push('/intelligent-import')}>
            <ArrowLeft size={16} strokeWidth={1.5} />
            Back
          </Button>
          <div className='min-w-0'>
            <h1 className='text-xl font-bold text-foreground truncate'>{currentJob?.filename || 'Import Job'}</h1>
            {currentJob?.id && <p className='text-sm text-muted-foreground'>ID: {currentJob.id}</p>}
          </div>
        </div>
        {isImportAdmin && currentJob?.id && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='border-destructive text-destructive hover:bg-destructive/10 shrink-0'
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 size={16} strokeWidth={1.5} />
            Delete import (admin)
          </Button>
        )}
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className='bg-card text-card-foreground border-border'>
          <DialogHeader>
            <DialogTitle>Delete this import?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className='text-sm text-foreground'>
              This permanently removes this import and all staging data. This cannot be undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => setDeleteOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button type='button' variant='destructive' loading={deleting} disabled={deleting} onClick={handleConfirmDeleteImport}>
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PipelineProgress status={status} />

      {error && (
        <Alert variant='destructive'>
          <AlertCircle size={16} />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isProcessing && (
        <div className='text-center py-20'>
          <div className='animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-lg font-medium text-foreground'>
            {currentJob?.functionStatus?.message ||
              currentJob?.function_status?.message ||
              (currentJob?.ingestMode === 'CONNECTOR' || currentJob?.ingest_mode === 'CONNECTOR'
                ? 'Pulling spend from connected accounting system…'
                : 'Processing your file...')}
          </p>
          <p className='text-sm text-muted-foreground mt-1'>Stage: {status}</p>
        </div>
      )}

      {renderContent}
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentJob: state.intelligentImport?.currentJob,
  error: state.intelligentImport?.error,
  cyfAdmin: state.profile?.details?.cyfAdmin === true,
  whiteLabelAdmin: state.profile?.details?.whiteLabelAdmin === true,
});

const mapDispatchToProps = {
  getImportStatus: IMPORT_ACTIONS.getImportStatus,
  clearJob: IMPORT_ACTIONS.clearJob,
  retryMatch: IMPORT_ACTIONS.retryMatch,
  commitImport: IMPORT_ACTIONS.commitImport,
  deleteImport: IMPORT_ACTIONS.deleteImport,
};

export default connect(mapStateToProps, mapDispatchToProps)(IntelligentImportJob);
